// pages/SecurityReviewPanel.jsx — tab "Security Review" di halaman Linux Login Audit
// Backend: backend/securityReview.js (/api/security-review/*), admin only, read-only di host target.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../context/AuthContext';
import { usePageAIContext } from '../context/AIPageContext';

const SEV = {
  critical: { label: 'Critical', cls: 'bg-red-600 text-white' },
  high: { label: 'High', cls: 'bg-red-100 text-red-700' },
  medium: { label: 'Medium', cls: 'bg-amber-100 text-amber-700' },
  low: { label: 'Low', cls: 'bg-sky-100 text-sky-700' },
  info: { label: 'Info', cls: 'bg-gray-100 text-gray-600' },
};
const STATUS = [
  ['open', 'Open'], ['in_progress', 'In progress'], ['fixed', 'Fixed (verify on next run)'],
  ['accepted_risk', 'Accepted risk'], ['false_positive', 'False positive'],
];
const STATUS_CLS = { open: 'text-red-700', in_progress: 'text-amber-700', fixed: 'text-emerald-700', accepted_risk: 'text-gray-600', false_positive: 'text-gray-500' };

function fmt(s) {
  if (!s) return '—';
  const d = new Date(/Z|\+/.test(s) ? s : s.replace(' ', 'T') + 'Z');
  return isNaN(d) ? s : d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function scoreTone(n) {
  if (n == null) return 'bg-gray-50 border-gray-200 text-gray-700';
  if (n >= 80) return 'bg-emerald-50 border-emerald-200 text-emerald-700';
  if (n >= 50) return 'bg-amber-50 border-amber-200 text-amber-700';
  return 'bg-red-50 border-red-200 text-red-700';
}
const Sev = ({ s }) => <span className={`inline-block rounded px-1.5 py-0.5 text-[11px] font-semibold ${(SEV[s] || SEV.info).cls}`}>{(SEV[s] || SEV.info).label}</span>;
const Chip = ({ children, cls }) => <span className={`inline-block rounded px-1.5 py-0.5 text-[11px] font-semibold ${cls}`}>{children}</span>;

function Block({ title, children, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-center gap-2 px-4 py-2.5 text-left hover:bg-gray-50">
        <span className={`text-xs text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`}>▶</span>
        <span className="font-semibold text-gray-800">{title}</span>
      </button>
      {open && <div className="border-t border-gray-100 p-3">{children}</div>}
    </div>
  );
}

function FindingRow({ f, host, runAt, onSaved }) {
  const st = f.status || {};
  const [status, setStatus] = useState(st.status || 'open');
  const [note, setNote] = useState(st.note || '');
  const [owner, setOwner] = useState(st.owner || '');
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const dirty = status !== (st.status || 'open') || note !== (st.note || '') || owner !== (st.owner || '');
  const save = async () => {
    setSaving(true);
    try { await api.put('/api/security-review/status', { host, checkId: f.id, status, note, owner }); onSaved(); }
    catch (e) { alert((e.response && e.response.data && e.response.data.error) || e.message); }
    finally { setSaving(false); }
  };
  const fixedButPresent = st.status === 'fixed' && st.updated_at && runAt && st.updated_at < runAt;
  return (
    <>
      <tr className="cursor-pointer align-top hover:bg-gray-50" onClick={() => setOpen(!open)}>
        <td className="px-3 py-2"><Sev s={f.severity} /></td>
        <td className="px-3 py-2">
          <div className="font-medium text-gray-800">{f.title}</div>
          <div className="text-[11px] text-gray-400">{f.category} · {f.id}</div>
        </td>
        <td className="px-3 py-2">
          {f.change === 'new' && <Chip cls="bg-red-100 text-red-700">NEW</Chip>}
          {f.change === 'persisting' && <Chip cls="bg-gray-100 text-gray-600">persisting</Chip>}
          {fixedButPresent && <div className="mt-1"><Chip cls="bg-amber-100 text-amber-700">marked fixed, still detected</Chip></div>}
        </td>
        <td className={`px-3 py-2 text-sm font-medium ${STATUS_CLS[st.status || 'open']}`}>
          {(STATUS.find((x) => x[0] === (st.status || 'open')) || [])[1]}
          {st.owner && <div className="text-[11px] font-normal text-gray-500">👤 {st.owner}</div>}
        </td>
        <td className="px-3 py-2 text-xs text-gray-400">{open ? '▲' : '▼'}</td>
      </tr>
      {open && (
        <tr className="bg-gray-50/60">
          <td />
          <td colSpan={4} className="px-3 pb-3 pt-1 text-sm">
            <div className="mb-2"><span className="font-semibold text-gray-600">Evidence: </span><span className="font-mono text-xs break-all text-gray-700">{f.evidence}</span></div>
            <div className="mb-3"><span className="font-semibold text-gray-600">Rekomendasi: </span>{f.recommendation}</div>
            <div className="flex flex-wrap items-start gap-2" onClick={(e) => e.stopPropagation()}>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm">
                {STATUS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
              <input value={owner} onChange={(e) => setOwner(e.target.value)} placeholder="PIC" className="w-32 rounded-lg border border-gray-300 px-2 py-1.5 text-sm" />
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Catatan perbaikan / tindak lanjut / alasan accepted risk…"
                className="min-w-[260px] flex-1 rounded-lg border border-gray-300 px-2 py-1.5 text-sm" />
              <button disabled={!dirty || saving} onClick={save} className="rounded-lg px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-40"
                style={{ backgroundColor: 'rgb(var(--brand-600))' }}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
            {st.updated_at && <div className="mt-1 text-[11px] text-gray-400">Last update {fmt(st.updated_at)} by {st.updated_by}</div>}
          </td>
        </tr>
      )}
    </>
  );
}

function toMarkdown(d) {
  const L = [];
  L.push(`# Security Review — ${d.label}`);
  L.push(`Run #${d.id} · ${fmt(d.run_at)} · oleh ${d.run_by} · periode log ${d.days} hari`);
  L.push(`Score: **${d.score}/100**${d.previous ? ` (sebelumnya ${d.previous.score} pada ${fmt(d.previous.run_at)})` : ''}`);
  const c = d.counts || {};
  L.push(`Critical ${c.critical || 0} · High ${c.high || 0} · Medium ${c.medium || 0} · Low ${c.low || 0} · Info ${c.info || 0}`, '');
  const fx = d.facts || {};
  if (fx.aws) L.push(`Instance ${fx.aws.instanceId} · public IP ${fx.aws.publicIp || '-'} · SG ${fx.aws.sgIds.join(', ')}`);
  L.push(`OS: ${fx.os} · kernel ${fx.kernel} · ${fx.openssl} · ${fx.nginxVersion}`, '');
  L.push('## Findings', '');
  (d.findings || []).forEach((f, i) => {
    const st = f.status || {};
    L.push(`### ${i + 1}. [${(SEV[f.severity] || {}).label}] ${f.title}${f.change === 'new' ? ' (NEW)' : ''}`);
    L.push(`- ID: ${f.id} · Kategori: ${f.category}`);
    L.push(`- Evidence: ${f.evidence}`);
    L.push(`- Rekomendasi: ${f.recommendation}`);
    L.push(`- Status: ${st.status || 'open'}${st.owner ? ' · PIC ' + st.owner : ''}${st.note ? ' · Catatan: ' + st.note : ''}`, '');
  });
  if (d.resolved && d.resolved.length) {
    L.push('## Resolved sejak run sebelumnya', '');
    d.resolved.forEach((f) => L.push(`- [${(SEV[f.severity] || {}).label}] ${f.title} (${f.id})`));
  }
  return L.join('\n');
}

export default function SecurityReviewPanel() {
  const [hosts, setHosts] = useState([]);
  const [host, setHost] = useState('');
  const [days, setDays] = useState(7);
  const [runs, setRuns] = useState([]);
  const [selId, setSelId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [sevFilter, setSevFilter] = useState('all');
  const [hideClosed, setHideClosed] = useState(false);

  useEffect(() => {
    api.get('/api/security-review/hosts').then((r) => { setHosts(r.data.hosts); setHost(r.data.defaultHost); })
      .catch((e) => setError((e.response && e.response.data && e.response.data.error) || e.message));
  }, []);

  const loadRuns = useCallback(async (selectLatest) => {
    if (!host) return;
    const r = await api.get(`/api/security-review/runs?host=${encodeURIComponent(host)}`);
    setRuns(r.data);
    if (selectLatest || !r.data.find((x) => x.id === selId)) setSelId(r.data.length ? r.data[0].id : null);
  }, [host, selId]);
  useEffect(() => { setDetail(null); setSelId(null); loadRuns(true); /* eslint-disable-next-line */ }, [host]);

  const loadDetail = useCallback(async () => {
    if (!selId) { setDetail(null); return; }
    const r = await api.get(`/api/security-review/runs/${selId}`);
    setDetail(r.data);
  }, [selId]);
  useEffect(() => { loadDetail(); }, [loadDetail]);

  const run = async () => {
    setRunning(true); setError('');
    try { const r = await api.post('/api/security-review/run', { host, days }); await loadRuns(false); setSelId(r.data.id); }
    catch (e) { setError((e.response && e.response.data && e.response.data.error) || e.message); await loadRuns(true); }
    finally { setRunning(false); }
  };

  // AI Assistant context — the selected run as markdown + run history for trend questions.
  const aiSummary = useMemo(() => {
    if (!detail) return null;
    const hist = (runs || []).slice(0, 10).map((r) => `- Run #${r.id} ${fmt(r.run_at)} score ${r.score}`).join('\n');
    return `Linux Login Audit (tab: Security Review) — host ${detail.label || detail.host}.\n\n${toMarkdown(detail)}${hist ? `\n\n## Run history (latest 10)\n${hist}` : ''}`;
  }, [detail, runs]);
  usePageAIContext('linux_login_audit', 'Linux Login Audit', aiSummary);

  const exportMd = () => {
    if (!detail) return;
    const blob = new Blob([toMarkdown(detail)], { type: 'text/markdown' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `security-review-${detail.host}-run${detail.id}.md`; a.click(); URL.revokeObjectURL(a.href);
  };

  const findings = ((detail && detail.findings) || []).filter((f) =>
    (sevFilter === 'all' || f.severity === sevFilter) &&
    (!hideClosed || !f.status || ['open', 'in_progress'].includes(f.status.status) || (f.status.status === 'fixed')));
  const fx = (detail && detail.facts) || {};
  const acc = fx.access || {};
  const delta = detail && detail.previous && detail.score != null ? detail.score - detail.previous.score : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-800">🛡️ Security Review — host publik</h2>
          <p className="text-sm text-gray-500">
            Audit eksposur otomatis: Security Group, konfigurasi nginx (proxy/rate-limit/TLS), SSH, SNMP, patch OS, dan anomali di access log.
            Semua pemeriksaan read-only. Setiap run disimpan dan dibandingkan dengan run sebelumnya; tiap temuan bisa diberi status, PIC dan catatan perbaikan.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={host} onChange={(e) => setHost(e.target.value)} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm">
            {hosts.map((h) => <option key={h.key} value={h.key}>{h.label}</option>)}
          </select>
          <select value={days} onChange={(e) => setDays(+e.target.value)} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm">
            {[1, 3, 7, 14, 30].map((d) => <option key={d} value={d}>Log {d} hari</option>)}
          </select>
          <button onClick={run} disabled={running || !host} className="rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            style={{ backgroundColor: 'rgb(var(--brand-600))' }}>{running ? 'Reviewing… (±1 menit)' : '▶ Run review'}</button>
          <button onClick={exportMd} disabled={!detail || !!detail.error} className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40">⬇ Report (.md)</button>
        </div>
      </div>
      {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}

      <div className="grid gap-4 xl:grid-cols-[260px_1fr]">
        {/* history */}
        <div className="rounded-xl border border-gray-200 bg-white">
          <div className="border-b border-gray-100 px-3 py-2 text-sm font-semibold text-gray-700">Riwayat run ({runs.length})</div>
          <div className="max-h-[70vh] overflow-auto divide-y divide-gray-100">
            {runs.map((r) => (
              <button key={r.id} onClick={() => setSelId(r.id)} className={`block w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${selId === r.id ? 'bg-teal-50' : ''}`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-800">#{r.id} · {fmt(r.run_at)}</span>
                  {r.error ? <Chip cls="bg-red-100 text-red-700">error</Chip> : <span className={`rounded border px-1.5 text-xs font-bold ${scoreTone(r.score)}`}>{r.score}</span>}
                </div>
                {r.counts && <div className="mt-0.5 text-[11px] text-gray-500">C{r.counts.critical} · H{r.counts.high} · M{r.counts.medium} · L{r.counts.low} · {r.run_by}</div>}
              </button>
            ))}
            {!runs.length && <div className="p-4 text-center text-sm text-gray-400">Belum ada run. Klik “Run review”.</div>}
          </div>
        </div>

        {/* detail */}
        <div className="space-y-4">
          {detail && detail.error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">Run #{detail.id} gagal: {detail.error}</div>}
          {detail && !detail.error && (
            <>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
                <div className={`rounded-xl border p-4 ${scoreTone(detail.score)}`}>
                  <div className="text-xs font-medium uppercase opacity-70">Score</div>
                  <div className="mt-1 text-2xl font-bold">{detail.score}<span className="text-sm opacity-60">/100</span></div>
                  {delta != null && <div className="text-xs opacity-80">{delta >= 0 ? '▲' : '▼'} {Math.abs(delta)} vs run #{detail.previous.id}</div>}
                </div>
                {['critical', 'high', 'medium', 'low', 'info'].map((k) => (
                  <button key={k} onClick={() => setSevFilter(sevFilter === k ? 'all' : k)}
                    className={`rounded-xl border bg-white p-4 text-left ${sevFilter === k ? 'ring-2 ring-teal-400' : 'border-gray-200'}`}>
                    <div className="text-xs font-medium uppercase text-gray-500">{SEV[k].label}</div>
                    <div className="mt-1 text-2xl font-bold text-gray-800">{(detail.counts || {})[k] || 0}</div>
                  </button>
                ))}
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-700">
                  <div className="text-xs font-medium uppercase opacity-70">Resolved</div>
                  <div className="mt-1 text-2xl font-bold">{(detail.resolved || []).length}</div>
                  <div className="text-xs opacity-70">vs run sebelumnya</div>
                </div>
              </div>

              <div className="text-xs text-gray-500">
                Run #{detail.id} · {fmt(detail.run_at)} · oleh {detail.run_by} · log {detail.days} hari ({acc.total || 0} request
                {acc.from ? `, ${fmt(acc.from)} → ${fmt(acc.to)}` : ''})
                {fx.aws && <> · {fx.aws.instanceId} · public IP {fx.aws.publicIp || '—'}</>}
              </div>

              <div className="rounded-xl border border-gray-200 bg-white">
                <div className="flex flex-wrap items-center gap-3 border-b border-gray-100 px-3 py-2">
                  <span className="text-sm font-semibold text-gray-700">Temuan ({findings.length})</span>
                  {sevFilter !== 'all' && <button onClick={() => setSevFilter('all')} className="text-xs text-teal-700">reset filter ✕</button>}
                  <label className="ml-auto flex items-center gap-1 text-sm text-gray-600">
                    <input type="checkbox" checked={hideClosed} onChange={(e) => setHideClosed(e.target.checked)} /> Sembunyikan accepted risk / false positive
                  </label>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                      <tr><th className="px-3 py-2">Severity</th><th className="px-3 py-2">Temuan</th><th className="px-3 py-2">Perubahan</th><th className="px-3 py-2">Status</th><th /></tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {findings.map((f) => <FindingRow key={detail.id + f.id} f={f} host={detail.host} runAt={detail.run_at} onSaved={loadDetail} />)}
                      {!findings.length && <tr><td colSpan={5} className="px-3 py-6 text-center text-gray-400">Tidak ada temuan untuk filter ini.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              {(detail.resolved || []).length > 0 && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm">
                  <div className="mb-1 font-semibold text-emerald-800">✅ Sudah tidak terdeteksi sejak run #{detail.previous.id}</div>
                  <ul className="list-disc pl-5 text-emerald-800">
                    {detail.resolved.map((f) => <li key={f.id}><Sev s={f.severity} /> {f.title} <span className="text-xs opacity-70">({f.id}{f.status ? ' · ' + f.status.status : ''})</span></li>)}
                  </ul>
                </div>
              )}

              <Block title="🌐 Eksposur jaringan (Security Group & listener)">
                {fx.aws ? (
                  <table className="mb-3 min-w-full text-sm">
                    <thead className="text-left text-xs uppercase text-gray-500"><tr><th className="py-1 pr-3">SG</th><th className="py-1 pr-3">Port</th><th className="py-1">Sumber</th></tr></thead>
                    <tbody className="divide-y divide-gray-100">
                      {fx.aws.rules.map((r, i) => (
                        <tr key={i}>
                          <td className="py-1 pr-3">{r.sg}</td>
                          <td className="py-1 pr-3 font-mono">{r.proto === '-1' ? 'ALL' : `${r.proto}/${r.from}${r.to !== r.from ? '-' + r.to : ''}`}</td>
                          <td className="py-1 font-mono text-xs">{r.cidrs.map((c) => <span key={c} className={`mr-2 ${c === '0.0.0.0/0' || c === '::/0' ? 'font-bold text-red-600' : ''}`}>{c}</span>)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : <div className="mb-2 text-sm text-gray-500">SG tidak terbaca.</div>}
                <div className="text-xs text-gray-500">Listener: {(fx.listen || []).map((l) => `${l.proto}/${l.port} ${l.proc}`).join(' · ')}</div>
              </Block>

              <Block title="🔀 Nginx — virtual host & proxy">
                <div className="mb-2 text-sm"><b>server_name:</b> {(fx.nginxServerNames || []).join(', ')}</div>
                <div className="mb-2 text-sm"><b>listen:</b> {(fx.nginxListen || []).join(' ')}</div>
                <table className="min-w-full text-sm">
                  <thead className="text-left text-xs uppercase text-gray-500"><tr><th className="py-1 pr-3">Location</th><th className="py-1">Upstream</th></tr></thead>
                  <tbody className="divide-y divide-gray-100">
                    {(fx.nginxProxies || []).map((p, i) => <tr key={i}><td className="py-1 pr-3 font-mono">{p.location}</td><td className="py-1 font-mono">{p.upstream}</td></tr>)}
                  </tbody>
                </table>
              </Block>

              <Block title="📈 Access log — IP & anomali">
                <div className="mb-2 text-sm text-gray-600">
                  Status: {Object.keys(acc.status || {}).sort((a, b) => acc.status[b] - acc.status[a]).slice(0, 10).map((k) => `${k}: ${acc.status[k]}`).join(' · ')}
                </div>
                <div className="grid gap-4 md:grid-cols-3">
                  {[['Top IP (semua)', acc.topIps], ['Top IP scanner', acc.topProbeIps], ['IP publik ke /sap', acc.topSapExt]].map(([t, rows]) => (
                    <div key={t}>
                      <div className="mb-1 text-xs font-semibold uppercase text-gray-500">{t}</div>
                      <table className="min-w-full text-sm"><tbody className="divide-y divide-gray-100">
                        {(rows || []).map((r) => <tr key={r.ip}><td className="py-0.5 font-mono">{r.ip}{r.private && <span className="ml-1 text-[10px] text-gray-400">private</span>}{r.known && <span className="ml-1 rounded bg-emerald-100 px-1 text-[10px] font-semibold text-emerald-700" title={r.known}>known</span>}</td><td className="py-0.5 text-right">{r.n}</td></tr>)}
                        {!(rows || []).length && <tr><td className="py-1 text-gray-400">—</td></tr>}
                      </tbody></table>
                    </div>
                  ))}
                </div>
                {(fx.errlog || []).length > 0 && <div className="mt-3 text-xs text-gray-500">error.log: {fx.errlog.join(' · ')}</div>}
              </Block>

              {(fx.vhosts || []).length > 0 && (
                <Block title="🌍 Vhost publik & sertifikat TLS">
                  <table className="min-w-full text-sm">
                    <thead className="text-left text-xs uppercase text-gray-500"><tr><th className="py-1 pr-3">Domain</th><th className="py-1 pr-3">HTTP</th><th className="py-1 pr-3">Halaman</th><th className="py-1 pr-3">Sertifikat s/d</th><th className="py-1">Sisa</th></tr></thead>
                    <tbody className="divide-y divide-gray-100">
                      {fx.vhosts.map((v) => (
                        <tr key={v.name}>
                          <td className="py-1 pr-3 font-mono">{v.name}</td>
                          <td className="py-1 pr-3">{v.error ? <span className="text-red-600">{v.error}</span> : v.status}{v.location ? ' → ' + v.location : ''}</td>
                          <td className="py-1 pr-3 text-gray-600">{v.title || '—'}</td>
                          <td className="py-1 pr-3">{fmt(v.certValidTo)}{v.authorized === false && <span className="ml-1 text-red-600">(invalid)</span>}</td>
                          <td className={`py-1 font-semibold ${v.certDaysLeft != null && v.certDaysLeft < 30 ? 'text-red-600' : 'text-emerald-700'}`}>{v.certDaysLeft != null ? v.certDaysLeft + ' hari' : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Block>
              )}

              <Block title="🔑 SSH & sistem">
                <div className="space-y-1 text-sm">
                  <div><b>OS:</b> {fx.os} · {fx.kernel} · {fx.openssl} · {fx.nginxVersion}</div>
                  <div><b>Security updates tertunda:</b> {fx.secUpdates}</div>
                  <div><b>sshd:</b> {Object.keys(fx.sshd || {}).map((k) => `${k}=${fx.sshd[k]}`).join(' · ')}</div>
                  <div><b>SSH accepted:</b> {(fx.sshAccepted || []).map((x) => `${x.what} (${x.n})${x.known ? ' [' + x.known + ']' : ''}`).join(' · ') || '—'}</div>
                  <div><b>SSH failed:</b> {(fx.sshFailed || []).map((x) => `${x.what} (${x.n})`).join(' · ') || '—'}</div>
                  <div><b>SNMP:</b> {(fx.snmp || []).join(' | ') || '—'}</div>
                  <div><b>iptables:</b> <span className="font-mono text-xs">{(fx.iptables || []).join(' ; ')}</span></div>
                </div>
              </Block>
            </>
          )}
          <KnownIps />
        </div>
      </div>
    </div>
  );
}

function KnownIps() {
  const [rows, setRows] = useState([]);
  const [ip, setIp] = useState(''); const [label, setLabel] = useState(''); const [err, setErr] = useState('');
  const load = useCallback(() => api.get('/api/security-review/known-ips').then((r) => setRows(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    setErr('');
    try { await api.post('/api/security-review/known-ips', { ip, label }); setIp(''); setLabel(''); load(); }
    catch (e) { setErr((e.response && e.response.data && e.response.data.error) || e.message); }
  };
  const del = async (x) => { if (!window.confirm(`Hapus ${x} dari daftar IP dikenal?`)) return; await api.delete(`/api/security-review/known-ips/${encodeURIComponent(x)}`); load(); };
  return (
    <Block title={`🏷️ IP / sumber dikenal (${rows.length})`}>
      <p className="mb-2 text-xs text-gray-500">Login SSH dari sumber di daftar ini tidak dianggap anomali, dan IP-nya diberi label “known” di tabel. Berlaku di run berikutnya. Prefix diakhiri titik, mis. <code>160.79.106.</code></p>
      <table className="mb-3 min-w-full text-sm"><tbody className="divide-y divide-gray-100">
        {rows.map((r) => (
          <tr key={r.ip}><td className="py-1 pr-3 font-mono">{r.ip}</td><td className="py-1 pr-3">{r.label}</td>
            <td className="py-1 pr-3 text-[11px] text-gray-400">{r.added_by}</td>
            <td className="py-1 text-right"><button onClick={() => del(r.ip)} className="text-xs text-red-600 hover:underline">hapus</button></td></tr>
        ))}
      </tbody></table>
      <div className="flex flex-wrap gap-2">
        <input value={ip} onChange={(e) => setIp(e.target.value)} placeholder="IP / prefix" className="w-40 rounded-lg border border-gray-300 px-2 py-1.5 text-sm font-mono" />
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Keterangan (mis. IP publik kantor Jakarta)" className="min-w-[220px] flex-1 rounded-lg border border-gray-300 px-2 py-1.5 text-sm" />
        <button onClick={add} disabled={!ip || !label} className="rounded-lg px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-40" style={{ backgroundColor: 'rgb(var(--brand-600))' }}>Tambah</button>
      </div>
      {err && <div className="mt-1 text-xs text-red-600">{err}</div>}
    </Block>
  );
}
